import { useState, useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

const IDLE_TIMEOUT = 20 * 60 * 1000; // 20 minutes
const WARNING_BEFORE = 2 * 60 * 1000; // 2 minutes before timeout
const WARNING_AT = IDLE_TIMEOUT - WARNING_BEFORE; // 18 minutes
const THROTTLE_MS = 1000; // throttle activity events to once per second

// Cross-tab activity coordination. All tabs write their latest activity here so
// an idle background tab doesn't log out a user actively working in another tab
// (logout destroys the shared server session, killing every tab at once).
const ACTIVITY_STORAGE_KEY = "administr:lastActivityAt";

// Disable the inactivity logout during local development — the countdown modal
// is noise when working on localhost. Toggle off by setting VITE_IDLE_TIMEOUT=1.
const IDLE_DISABLED =
  import.meta.env.VITE_IDLE_TIMEOUT !== "1" &&
  (import.meta.env.DEV ||
    (typeof window !== "undefined" &&
      ["localhost", "127.0.0.1"].includes(window.location.hostname)));

function readSharedActivity(): number {
  try {
    const raw = localStorage.getItem(ACTIVITY_STORAGE_KEY);
    const parsed = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeSharedActivity(ts: number) {
  try {
    localStorage.setItem(ACTIVITY_STORAGE_KEY, String(ts));
  } catch {
    /* private browsing / storage full — local ref still works per-tab */
  }
}

export function useIdleTimeout() {
  const queryClient = useQueryClient();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(WARNING_BEFORE / 1000);

  const lastActivityRef = useRef(Date.now());
  const throttleRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  // Effective last-activity = max(this tab, any other tab via localStorage).
  const effectiveLastActivity = useCallback(() => {
    return Math.max(lastActivityRef.current, readSharedActivity());
  }, []);

  const resetTimer = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    writeSharedActivity(now);
    setShowWarning(false);
    setRemainingSeconds(WARNING_BEFORE / 1000);
  }, []);

  const logout = useCallback(async () => {
    try {
      queryClient.clear();
      localStorage.removeItem("selectedRole");
      localStorage.removeItem(ACTIVITY_STORAGE_KEY);
      await fetch("/api/logout", { method: "POST" });
    } catch {
      // Redirect regardless
    }
    window.location.replace("/");
  }, [queryClient]);

  const stayActive = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Activity listener (throttled)
  useEffect(() => {
    const onActivity = () => {
      const now = Date.now();
      if (now - throttleRef.current < THROTTLE_MS) return;
      throttleRef.current = now;

      // Only reset if we're not already in warning state — once the warning is
      // up, the user must explicitly click "Stay Logged In".
      if (!showWarning) {
        lastActivityRef.current = now;
        writeSharedActivity(now);
      }
    };

    const events = ["mousemove", "keydown", "mousedown", "scroll", "touchstart"] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [showWarning]);

  // Periodic check + instant check when a backgrounded tab is foregrounded
  // (browsers throttle/freeze timers in background tabs, so the interval alone
  // can lag by minutes — visibilitychange closes that gap).
  useEffect(() => {
    if (IDLE_DISABLED) return;

    const check = () => {
      const elapsed = Date.now() - effectiveLastActivity();

      if (elapsed >= IDLE_TIMEOUT) {
        clearInterval(intervalRef.current);
        logout();
      } else if (elapsed >= WARNING_AT) {
        setShowWarning(true);
        const left = Math.ceil((IDLE_TIMEOUT - elapsed) / 1000);
        setRemainingSeconds(Math.max(0, left));
      } else {
        // Another tab's activity (or our own) pushed us back under the
        // threshold — clear the warning if it was showing.
        setShowWarning((prev) => (prev ? false : prev));
      }
    };

    intervalRef.current = setInterval(check, 1000);

    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [logout, effectiveLastActivity]);

  return { showWarning, remainingSeconds, stayActive, logout };
}
